import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail, API } from "../lib/apiClient";
import { useAuth } from "../lib/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  ArrowLeft, Trash2, Upload, Plus, ExternalLink, Youtube,
  Paperclip, FileText, FileImage, FileAudio, FileVideo, File as FileIcon,
  Download,
} from "lucide-react";
import {
  Dialog as AttachDialog, DialogContent as AttachDialogContent,
  DialogHeader as AttachDialogHeader, DialogTitle as AttachDialogTitle,
} from "../components/ui/dialog";

const COST_CATEGORIES = [
  { v: "permits", l: "Official Permits" },
  { v: "star", l: "Star / Headliner" },
  { v: "band", l: "Band" },
  { v: "performance", l: "Other Performance" },
  { v: "staffing", l: "Staffing" },
  { v: "media", l: "Media & Marketing" },
  { v: "operation", l: "Operations & Ticketing Staff" },
  { v: "production", l: "Production" },
  { v: "venue", l: "Venue" },
  { v: "cashflow", l: "Cash Flow" },
  { v: "other", l: "Other" },
];

const SALES_CHANNELS = [
  { v: "box_office", l: "Box Office" },
  { v: "online", l: "Online" },
  { v: "partner", l: "Partner Platform" },
  { v: "corporate", l: "Corporate / B2B" },
  { v: "sponsor", l: "Sponsor / Barter" },
  { v: "other", l: "Other" },
];

const PALETTE = ["#2A5B75", "#3A4B3A", "#C05A45", "#D4AF37", "#8C7A6B", "#4A6E7A"];

export default function EventDetail() {
  const { id } = useParams();
  const { fmt, convert } = useAuth();
  const [event, setEvent] = useState(null);
  const [summary, setSummary] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [costs, setCosts] = useState([]);
  const [sales, setSales] = useState([]);
  const [media, setMedia] = useState([]);

  const reloadAll = useCallback(async () => {
    const [e, s, t, sp, c, sl, m] = await Promise.all([
      api.get(`/events/${id}`).then((r) => r.data),
      api.get(`/events/${id}/summary`).then((r) => r.data),
      api.get(`/events/${id}/tickets`).then((r) => r.data),
      api.get(`/events/${id}/sponsors`).then((r) => r.data),
      api.get(`/events/${id}/costs`).then((r) => r.data),
      api.get(`/events/${id}/sales`).then((r) => r.data),
      api.get(`/events/${id}/media`).then((r) => r.data),
    ]);
    setEvent(e); setSummary(s); setTickets(t); setSponsors(sp);
    setCosts(c); setSales(sl); setMedia(m);
  }, [id]);

  useEffect(() => { reloadAll(); }, [reloadAll]);

  if (!event) return <div className="text-sm text-[var(--text-2)]">Loading…</div>;

  return (
    <div className="max-w-7xl">
      <Link to="/events" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--text-2)] mb-4 hover:text-[var(--text-1)]" data-testid="back-events">
        <ArrowLeft className="w-3 h-3" /> All Events
      </Link>

      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[var(--text-2)] mb-2 mono">
            {event.date} · {event.status?.toUpperCase()}
          </div>
          <h1 className="font-serif text-5xl leading-tight" data-testid="event-title">{event.name}</h1>
          <div className="mt-3 text-sm text-[var(--text-2)]">
            {event.main_character && <>Starring <b className="text-[var(--text-1)]">{event.main_character}</b>{event.aux_characters?.length ? " · with " + event.aux_characters.join(", ") : ""}</>}
          </div>
          <div className="mt-1 text-sm text-[var(--text-2)]">
            {event.venue}{event.location ? ` · ${event.location}` : ""}
          </div>
        </div>
      </div>

      {/* KPI strip */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-[var(--border-1)] bg-white mb-8">
          <KPI label="Budget" value={fmt(summary.budget_total)} />
          <KPI label="Actual" value={fmt(summary.actual_total)} accent={summary.actual_total > summary.budget_total} />
          <KPI label="Sales" value={fmt(summary.sales_total)} />
          <KPI label="Sponsors" value={fmt(summary.sponsors_total)} />
          <KPI label="Profit" value={fmt(summary.profit)} accent={summary.profit < 0} />
        </div>
      )}

      <Tabs defaultValue="detail" className="w-full">
        <TabsList className="rounded-none bg-white border border-[var(--border-1)] p-0 h-auto flex-wrap justify-start" data-testid="event-tabs">
          {[
            ["detail", "Detail"],
            ["accounting", "Event Accounting"],
            ["sponsors", "Sponsorships"],
            ["media", "Media Assets"],
            ["ticketing", "Ticketing"],
          ].map(([v, l]) => (
            <TabsTrigger key={v} value={v} className="rounded-none data-[state=active]:bg-[var(--primary)] data-[state=active]:text-white px-6 py-3 text-xs uppercase tracking-widest" data-testid={`tab-${v}`}>
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="detail" className="pt-6">
          <DetailPane event={event} />
        </TabsContent>

        <TabsContent value="accounting" className="pt-6">
          <AccountingPane
            eventId={id} costs={costs} sales={sales} sponsors={sponsors}
            summary={summary} onChange={reloadAll} fmt={fmt} convert={convert}
            onedriveUrl={event.onedrive_url}
          />
        </TabsContent>

        <TabsContent value="sponsors" className="pt-6">
          <SponsorsPane eventId={id} sponsors={sponsors} onChange={reloadAll} fmt={fmt} />
        </TabsContent>

        <TabsContent value="media" className="pt-6">
          <MediaPane eventId={id} youtubeUrl={event.youtube_url} media={media} onChange={reloadAll} />
        </TabsContent>

        <TabsContent value="ticketing" className="pt-6">
          <TicketingPane eventId={id} tickets={tickets} onChange={reloadAll} fmt={fmt} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function KPI({ label, value, accent }) {
  return (
    <div className="border-r border-[var(--border-1)] last:border-r-0 p-5">
      <div className="text-[10px] uppercase tracking-widest text-[var(--text-2)] mb-2">{label}</div>
      <div className={`font-serif text-2xl ${accent ? "text-[var(--accent)]" : ""}`}>{value}</div>
    </div>
  );
}

function DetailPane({ event }) {
  const rows = [
    ["Event name", event.name],
    ["Date", event.date],
    ["Status", event.status],
    ["Main character", event.main_character || "—"],
    ["Auxiliary", event.aux_characters?.join(", ") || "—"],
    ["Venue", event.venue || "—"],
    ["Location", event.location || "—"],
    ["Description", event.description || "—"],
    ["OneDrive Excel", event.onedrive_url ? <a className="underline mono text-xs" href={event.onedrive_url} target="_blank" rel="noreferrer">{event.onedrive_url}</a> : "—"],
    ["YouTube", event.youtube_url ? <a className="underline mono text-xs" href={event.youtube_url} target="_blank" rel="noreferrer">{event.youtube_url}</a> : "—"],
  ];
  return (
    <div className="card-sharp">
      <table className="w-full">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k} className="border-b border-[var(--border-1)] last:border-b-0">
              <td className="ledger-head px-6 py-3 w-1/3 align-top">{k}</td>
              <td className="px-6 py-3 text-sm">{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AccountingPane({ eventId, costs, sales, sponsors, summary, onChange, fmt, convert, onedriveUrl }) {
  const [costForm, setCostForm] = useState({ kind: "actual", category: "permits", description: "", amount_usd: "", source: "", payment_gateway: "" });
  const [saleForm, setSaleForm] = useState({ channel: "online", description: "", amount_usd: "", quantity: "" });

  const addCost = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/events/${eventId}/costs`, {
        ...costForm, amount_usd: parseFloat(costForm.amount_usd || 0),
      });
      toast.success("Cost added"); setCostForm({ ...costForm, description: "", amount_usd: "", source: "", payment_gateway: "" });
      onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const addSale = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/events/${eventId}/sales`, {
        ...saleForm, amount_usd: parseFloat(saleForm.amount_usd || 0), quantity: parseInt(saleForm.quantity || 0),
      });
      toast.success("Sale added"); setSaleForm({ ...saleForm, description: "", amount_usd: "", quantity: "" });
      onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // aggregate by category
  const byCat = COST_CATEGORIES.map((c) => {
    const budget = costs.filter((x) => x.category === c.v && x.kind === "budget").reduce((a, b) => a + b.amount_usd, 0);
    const actual = costs.filter((x) => x.category === c.v && x.kind === "actual").reduce((a, b) => a + b.amount_usd, 0);
    return { name: c.l.split(" ")[0], Budget: convert(budget), Actual: convert(actual) };
  }).filter((r) => r.Budget || r.Actual);

  const salesByChannel = SALES_CHANNELS.map((c) => {
    const v = sales.filter((s) => s.channel === c.v).reduce((a, b) => a + b.amount_usd, 0);
    return { name: c.l, value: convert(v) };
  }).filter((r) => r.value > 0);

  // Revenue projection: cumulative sales over time
  const sortedSales = [...sales].sort((a, b) => a.ts.localeCompare(b.ts));
  let cum = 0;
  const projection = sortedSales.map((s) => {
    cum += s.amount_usd;
    return { date: (s.ts || "").slice(0, 10), Cumulative: convert(cum) };
  });

  return (
    <div className="space-y-8">
      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="A. Budgeting Forecast vs B. Actual Cost Ledger">
          {byCat.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={byCat}>
                <CartesianGrid stroke="#EBE8E0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#4A4A4A", fontFamily: "IBM Plex Mono" }} />
                <YAxis tick={{ fontSize: 10, fill: "#4A4A4A", fontFamily: "IBM Plex Mono" }} />
                <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #EBE8E0" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Budget" fill="#3A4B3A" />
                <Bar dataKey="Actual" fill="#C05A45" />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty label="No cost data yet" />}
        </ChartCard>

        <ChartCard title="C. Sales Analytics (by channel)">
          {salesByChannel.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={salesByChannel} dataKey="value" nameKey="name" outerRadius={90} label={{ fontSize: 10, fontFamily: "IBM Plex Mono" }}>
                  {salesByChannel.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #EBE8E0" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty label="No sales yet" />}
        </ChartCard>

        <ChartCard title="D. Revenue Projection (cumulative)" span2>
          {projection.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={projection}>
                <CartesianGrid stroke="#EBE8E0" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#4A4A4A", fontFamily: "IBM Plex Mono" }} />
                <YAxis tick={{ fontSize: 10, fill: "#4A4A4A", fontFamily: "IBM Plex Mono" }} />
                <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #EBE8E0" }} />
                <Line type="monotone" dataKey="Cumulative" stroke="#2A5B75" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <Empty label="No sales data — chart will populate as you log sales" />}
        </ChartCard>
      </div>

      {/* OneDrive iframe */}
      {onedriveUrl && (
        <div className="card-sharp p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif text-xl">OneDrive Excel (live)</h3>
            <a href={onedriveUrl} target="_blank" rel="noreferrer" className="text-xs mono flex items-center gap-1 underline">
              <ExternalLink className="w-3 h-3" /> Open
            </a>
          </div>
          <div className="border border-[var(--border-1)]" style={{ height: 500 }}>
            <iframe src={onedriveUrl} title="OneDrive Excel" width="100%" height="100%" frameBorder="0" />
          </div>
        </div>
      )}

      {/* Cost entry */}
      <div className="card-sharp p-6">
        <h3 className="font-serif text-xl mb-4">Add Cost Entry</h3>
        <form onSubmit={addCost} className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="cost-form">
          <Select value={costForm.kind} onValueChange={(v) => setCostForm({ ...costForm, kind: v })}>
            <SelectTrigger className="rounded-none" data-testid="cost-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="budget">Budget</SelectItem>
              <SelectItem value="actual">Actual</SelectItem>
            </SelectContent>
          </Select>
          <Select value={costForm.category} onValueChange={(v) => setCostForm({ ...costForm, category: v })}>
            <SelectTrigger className="rounded-none" data-testid="cost-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {COST_CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="cost-desc" placeholder="Description" value={costForm.description} onChange={(e) => setCostForm({ ...costForm, description: e.target.value })} required className="rounded-none col-span-2" />
          <Input data-testid="cost-amount" placeholder="Amount USD" type="number" step="0.01" value={costForm.amount_usd} onChange={(e) => setCostForm({ ...costForm, amount_usd: e.target.value })} required className="rounded-none" />
          <Input data-testid="cost-source" placeholder="Source" value={costForm.source} onChange={(e) => setCostForm({ ...costForm, source: e.target.value })} className="rounded-none" />
          <Input data-testid="cost-gateway" placeholder="Payment Gateway" value={costForm.payment_gateway} onChange={(e) => setCostForm({ ...costForm, payment_gateway: e.target.value })} className="rounded-none col-span-5" />
          <Button type="submit" data-testid="cost-submit" className="rounded-none bg-[var(--primary)] hover:bg-[var(--primary)]/90 text-white">Add</Button>
        </form>
      </div>

      {/* Cost table */}
      <LedgerTable
        title="Cost Ledger"
        cols={["Date", "Kind", "Category", "Description", "Source", "Gateway", "Files", "Amount"]}
        rows={costs}
        render={(c) => [
          <span className="mono text-xs">{(c.ts || "").slice(0, 10)}</span>,
          <span className={`mono text-[10px] uppercase tracking-widest ${c.kind === "budget" ? "text-[var(--primary)]" : "text-[var(--accent)]"}`}>{c.kind}</span>,
          COST_CATEGORIES.find((x) => x.v === c.category)?.l || c.category,
          c.description,
          c.source,
          c.payment_gateway,
          <AttachmentsButton eventId={eventId} attachedType="cost" attachedId={c.id} />,
          <span className="mono">{fmt(c.amount_usd)}</span>,
        ]}
        onDelete={async (row) => { await api.delete(`/costs/${row.id}`); onChange(); }}
        testid="costs-table"
      />

      {/* Sale entry */}
      <div className="card-sharp p-6">
        <h3 className="font-serif text-xl mb-4">Add Sale Entry</h3>
        <form onSubmit={addSale} className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="sale-form">
          <Select value={saleForm.channel} onValueChange={(v) => setSaleForm({ ...saleForm, channel: v })}>
            <SelectTrigger className="rounded-none" data-testid="sale-channel"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SALES_CHANNELS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="sale-desc" placeholder="Description" value={saleForm.description} onChange={(e) => setSaleForm({ ...saleForm, description: e.target.value })} className="rounded-none col-span-2" />
          <Input data-testid="sale-qty" placeholder="Qty" type="number" value={saleForm.quantity} onChange={(e) => setSaleForm({ ...saleForm, quantity: e.target.value })} className="rounded-none" />
          <Input data-testid="sale-amount" placeholder="Amount USD" type="number" step="0.01" value={saleForm.amount_usd} onChange={(e) => setSaleForm({ ...saleForm, amount_usd: e.target.value })} required className="rounded-none" />
          <Button type="submit" data-testid="sale-submit" className="rounded-none bg-[var(--primary)] hover:bg-[var(--primary)]/90 text-white col-span-5 md:col-span-1">Add</Button>
        </form>
      </div>

      <LedgerTable
        title="Sales Ledger"
        cols={["Date", "Channel", "Description", "Qty", "Files", "Amount"]}
        rows={sales}
        render={(s) => [
          <span className="mono text-xs">{(s.ts || "").slice(0, 10)}</span>,
          SALES_CHANNELS.find((x) => x.v === s.channel)?.l || s.channel,
          s.description,
          <span className="mono">{s.quantity}</span>,
          <AttachmentsButton eventId={eventId} attachedType="sale" attachedId={s.id} />,
          <span className="mono">{fmt(s.amount_usd)}</span>,
        ]}
        onDelete={async (row) => { await api.delete(`/sales/${row.id}`); onChange(); }}
        testid="sales-table"
      />
    </div>
  );
}

function LedgerTable({ title, cols, rows, render, onDelete, testid }) {
  return (
    <div className="card-sharp" data-testid={testid}>
      <div className="p-6 pb-3 flex items-center justify-between">
        <h3 className="font-serif text-xl">{title}</h3>
        <span className="text-[10px] uppercase tracking-widest text-[var(--text-2)] mono">{rows.length} entries</span>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="border-t border-[var(--border-1)]">
            {cols.map((c, i) => (
              <TableHead key={c} className={`ledger-head ${i === cols.length - 1 ? "text-right" : ""}`}>{c}</TableHead>
            ))}
            <TableHead className="w-8" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow><TableCell colSpan={cols.length + 1} className="text-center py-8 text-[var(--text-2)] text-sm">No entries yet</TableCell></TableRow>
          ) : rows.map((row) => {
            const cells = render(row);
            return (
              <TableRow key={row.id} className="hover:bg-[var(--bg)]">
                {cells.map((cell, i) => (
                  <TableCell key={i} className={`text-sm ${i === cells.length - 1 ? "text-right" : ""}`}>{cell}</TableCell>
                ))}
                <TableCell className="text-right">
                  <button onClick={() => onDelete(row)} className="text-[var(--text-2)] hover:text-[var(--accent)]" data-testid={`del-${row.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function ChartCard({ title, span2, children }) {
  return (
    <div className={`card-sharp p-6 ${span2 ? "lg:col-span-2" : ""}`}>
      <h3 className="font-serif text-lg mb-4">{title}</h3>
      {children}
    </div>
  );
}
function Empty({ label }) {
  return <div className="h-[280px] flex items-center justify-center text-sm text-[var(--text-2)]">{label}</div>;
}

function SponsorsPane({ eventId, sponsors, onChange, fmt }) {
  const [f, setF] = useState({ name: "", tier: "", amount_usd: "", contact: "", notes: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/events/${eventId}/sponsors`, { ...f, amount_usd: parseFloat(f.amount_usd || 0) });
      toast.success("Sponsor added"); setF({ name: "", tier: "", amount_usd: "", contact: "", notes: "" }); onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (
    <div className="space-y-6">
      <div className="card-sharp p-6">
        <h3 className="font-serif text-xl mb-4">Add Sponsor</h3>
        <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="sponsor-form">
          <Input placeholder="Sponsor name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required className="rounded-none" data-testid="sponsor-name" />
          <Input placeholder="Tier (Gold, Silver…)" value={f.tier} onChange={(e) => setF({ ...f, tier: e.target.value })} className="rounded-none" data-testid="sponsor-tier" />
          <Input placeholder="Amount USD" type="number" step="0.01" value={f.amount_usd} onChange={(e) => setF({ ...f, amount_usd: e.target.value })} className="rounded-none" data-testid="sponsor-amount" />
          <Input placeholder="Contact" value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} className="rounded-none" data-testid="sponsor-contact" />
          <Input placeholder="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="rounded-none col-span-2" data-testid="sponsor-notes" />
          <Button type="submit" className="rounded-none bg-[var(--primary)] hover:bg-[var(--primary)]/90 text-white col-span-6 md:col-span-1" data-testid="sponsor-submit">Add</Button>
        </form>
      </div>

      <LedgerTable
        title="Sponsorships"
        cols={["Sponsor", "Tier", "Contact", "Notes", "Files", "Amount"]}
        rows={sponsors}
        render={(s) => [
          s.name,
          <span className="mono text-xs">{s.tier}</span>,
          s.contact,
          s.notes,
          <AttachmentsButton eventId={eventId} attachedType="sponsor" attachedId={s.id} />,
          <span className="mono">{fmt(s.amount_usd)}</span>,
        ]}
        onDelete={async (row) => { await api.delete(`/sponsors/${row.id}`); onChange(); }}
        testid="sponsors-table"
      />
    </div>
  );
}

function TicketingPane({ eventId, tickets, onChange, fmt }) {
  const [f, setF] = useState({ name: "", price_usd: "", quantity: "", sold: 0 });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/events/${eventId}/tickets`, {
        ...f, price_usd: parseFloat(f.price_usd), quantity: parseInt(f.quantity), sold: parseInt(f.sold || 0),
      });
      toast.success("Tier added"); setF({ name: "", price_usd: "", quantity: "", sold: 0 }); onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (
    <div className="space-y-6">
      <div className="card-sharp p-6">
        <h3 className="font-serif text-xl mb-4">Add Ticket Tier</h3>
        <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="ticket-form">
          <Input placeholder="Tier name (VIP, Standard…)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required className="rounded-none col-span-2" data-testid="ticket-name" />
          <Input placeholder="Price USD" type="number" step="0.01" value={f.price_usd} onChange={(e) => setF({ ...f, price_usd: e.target.value })} required className="rounded-none" data-testid="ticket-price" />
          <Input placeholder="Quantity" type="number" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} required className="rounded-none" data-testid="ticket-qty" />
          <Input placeholder="Sold" type="number" value={f.sold} onChange={(e) => setF({ ...f, sold: e.target.value })} className="rounded-none" data-testid="ticket-sold" />
          <Button type="submit" className="rounded-none bg-[var(--primary)] hover:bg-[var(--primary)]/90 text-white col-span-5 md:col-span-1" data-testid="ticket-submit">Add tier</Button>
        </form>
      </div>

      <LedgerTable
        title="Ticket Tiers"
        cols={["Tier", "Price", "Capacity", "Sold", "Remaining", "Revenue"]}
        rows={tickets}
        render={(t) => [
          t.name,
          <span className="mono">{fmt(t.price_usd)}</span>,
          <span className="mono">{t.quantity}</span>,
          <span className="mono">{t.sold}</span>,
          <span className="mono">{t.quantity - t.sold}</span>,
          <span className="mono">{fmt(t.price_usd * t.sold)}</span>,
        ]}
        onDelete={async (row) => { await api.delete(`/tickets/${row.id}`); onChange(); }}
        testid="tickets-table"
      />
    </div>
  );
}

function MediaPane({ eventId, youtubeUrl, media, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("poster");
  const [blobs, setBlobs] = useState({});

  const CATS = [
    { v: "poster", l: "Posters" },
    { v: "contract", l: "Contracts" },
    { v: "permit", l: "Permits" },
    { v: "receipt", l: "Receipts" },
    { v: "document", l: "Documents" },
    { v: "audio", l: "Audio" },
    { v: "video", l: "Video" },
    { v: "other", l: "Other" },
  ];

  // Only load blobs for images (posters). Others are downloaded on click.
  useEffect(() => {
    (async () => {
      const out = {};
      for (const m of media) {
        if ((m.content_type || "").startsWith("image/")) {
          try {
            const res = await fetch(`${API}/media/file/${m.storage_path}`, { credentials: "include" });
            if (res.ok) out[m.id] = URL.createObjectURL(await res.blob());
          } catch {}
        }
      }
      setBlobs(out);
    })();
    return () => Object.values(blobs).forEach((u) => URL.revokeObjectURL(u));
    // eslint-disable-next-line
  }, [media]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    try {
      await api.post(`/events/${eventId}/media`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded"); onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const ytEmbed = youtubeUrl
    ? youtubeUrl.replace("watch?v=", "embed/").replace("youtu.be/", "youtube.com/embed/")
    : null;

  // Group by category
  const grouped = CATS.map((c) => ({
    ...c, items: media.filter((m) => (m.category || "poster") === c.v && !m.attached_type),
  }));

  return (
    <div className="space-y-6">
      <div className="card-sharp p-6">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h3 className="font-serif text-xl">Media Library</h3>
          <div className="flex items-center gap-3">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="rounded-none w-40 h-9" data-testid="media-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
              </SelectContent>
            </Select>
            <label
              className="cursor-pointer inline-flex items-center gap-2 border border-[var(--primary)] px-4 py-2 text-xs uppercase tracking-widest hover:bg-[var(--primary)] hover:text-white transition-colors"
              data-testid="upload-label"
            >
              <Upload className="w-3 h-3" /> {uploading ? "Uploading…" : "Upload file"}
              <input
                type="file"
                className="hidden"
                onChange={handleFile}
                disabled={uploading}
                data-testid="upload-input"
              />
            </label>
          </div>
        </div>
        <p className="text-xs text-[var(--text-2)] mb-6">
          Any file type up to 50MB. Access is restricted to admins and the uploader.
        </p>

        {grouped.every((g) => g.items.length === 0) ? (
          <div className="text-sm text-[var(--text-2)]">No files uploaded yet.</div>
        ) : (
          <div className="space-y-8" data-testid="media-groups">
            {grouped.map((g) => g.items.length > 0 && (
              <div key={g.v} data-testid={`media-group-${g.v}`}>
                <div className="ledger-head mb-3">{g.l} · {g.items.length}</div>
                {g.v === "poster" ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="posters-grid">
                    {g.items.map((m) => (
                      <div key={m.id} className="border border-[var(--border-1)] aspect-[2/3] bg-[var(--bg)] relative group">
                        {blobs[m.id] ? (
                          <img src={blobs[m.id]} alt={m.original_filename} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xs text-[var(--text-2)]">Loading…</div>
                        )}
                        <button
                          onClick={async () => { await api.delete(`/media/${m.id}`); onChange(); }}
                          className="absolute top-2 right-2 bg-white/90 p-1 border border-[var(--border-1)] opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`del-poster-${m.id}`}
                        >
                          <Trash2 className="w-3 h-3 text-[var(--accent)]" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {g.items.map((m) => <FileRow key={m.id} m={m} blobs={blobs} onDelete={async () => { await api.delete(`/media/${m.id}`); onChange(); }} />)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card-sharp p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl flex items-center gap-2"><Youtube className="w-5 h-5 text-[var(--accent)]" /> YouTube</h3>
        </div>
        {ytEmbed ? (
          <div className="aspect-video border border-[var(--border-1)]">
            <iframe src={ytEmbed} title="YouTube" width="100%" height="100%" frameBorder="0" allowFullScreen />
          </div>
        ) : (
          <div className="text-sm text-[var(--text-2)]">No YouTube URL set. Edit the event to add one.</div>
        )}
      </div>
    </div>
  );
}

/* --------------------- File helpers --------------------- */
function fileIconFor(ctype) {
  if (!ctype) return FileIcon;
  if (ctype.startsWith("image/")) return FileImage;
  if (ctype.startsWith("audio/")) return FileAudio;
  if (ctype.startsWith("video/")) return FileVideo;
  if (ctype.includes("pdf") || ctype.startsWith("text/") || ctype.includes("word") || ctype.includes("sheet"))
    return FileText;
  return FileIcon;
}

function humanSize(n) {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  const kb = n / 1024;
  return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.round(kb)} KB`;
}

async function downloadFile(m) {
  try {
    const res = await fetch(`${API}/media/file/${m.storage_path}`, { credentials: "include" });
    if (!res.ok) { toast.error("Download failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = m.original_filename || "file";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 3000);
  } catch { toast.error("Download failed"); }
}

function FileRow({ m, onDelete }) {
  const Icon = fileIconFor(m.content_type);
  return (
    <div className="border border-[var(--border-1)] p-3 flex items-center gap-3 bg-white" data-testid={`file-row-${m.id}`}>
      <Icon className="w-5 h-5 text-[var(--primary)] shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-sm truncate">{m.original_filename}</div>
        <div className="text-[10px] mono text-[var(--text-2)] uppercase tracking-widest">
          {(m.category || "other")} · {humanSize(m.size)} · {(m.created_at || "").slice(0, 10)}
        </div>
      </div>
      <button onClick={() => downloadFile(m)} className="p-1 text-[var(--text-2)] hover:text-[var(--primary)]" title="Download" data-testid={`dl-${m.id}`}>
        <Download className="w-4 h-4" />
      </button>
      <button onClick={onDelete} className="p-1 text-[var(--text-2)] hover:text-[var(--accent)]" title="Delete" data-testid={`del-${m.id}`}>
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

/* --------------------- Attachment cell (used in Cost/Sale/Sponsor tables) --------------------- */
function AttachmentsButton({ eventId, attachedType, attachedId, onChange }) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [count, setCount] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/events/${eventId}/media`, {
      params: { attached_type: attachedType, attached_id: attachedId },
    });
    setFiles(data);
    setCount(data.length);
  };
  useEffect(() => { load().catch(() => setCount(0)); /* eslint-disable-next-line */ }, [attachedId]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", "document");
    fd.append("attached_type", attachedType);
    fd.append("attached_id", attachedId);
    try {
      await api.post(`/events/${eventId}/media`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File attached");
      await load();
      onChange && onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const del = async (id) => {
    await api.delete(`/media/${id}`); await load(); onChange && onChange();
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-[var(--text-2)] hover:text-[var(--primary)] text-xs mono"
        title="Attachments"
        data-testid={`attach-btn-${attachedId}`}
      >
        <Paperclip className="w-3 h-3" />
        <span>{count ?? "·"}</span>
      </button>
      <AttachDialog open={open} onOpenChange={setOpen}>
        <AttachDialogContent className="rounded-none max-w-lg">
          <AttachDialogHeader>
            <AttachDialogTitle className="font-serif text-xl">Attachments</AttachDialogTitle>
          </AttachDialogHeader>
          <div className="space-y-3" data-testid={`attach-dialog-${attachedId}`}>
            {files.length === 0 && (
              <div className="text-sm text-[var(--text-2)]">No files attached yet.</div>
            )}
            {files.map((m) => <FileRow key={m.id} m={m} onDelete={() => del(m.id)} />)}
            <label className="cursor-pointer inline-flex items-center gap-2 border border-[var(--primary)] px-4 py-2 text-xs uppercase tracking-widest hover:bg-[var(--primary)] hover:text-white transition-colors">
              <Upload className="w-3 h-3" /> {uploading ? "Uploading…" : "Attach file"}
              <input type="file" className="hidden" onChange={upload} disabled={uploading} data-testid={`attach-upload-${attachedId}`} />
            </label>
            <div className="text-[11px] text-[var(--text-2)]">
              Access is restricted to admins and the uploader.
            </div>
          </div>
        </AttachDialogContent>
      </AttachDialog>
    </>
  );
}
